import React, { useState, useEffect } from 'react';
import CustomerLayout from '../../components/layout/CustomerLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { customerService } from '../../services/customerService';
import {
  FiDollarSign,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';

const CustomerTransactions = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterType]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20
      };

      if (filterType) {
        params.type = filterType;
      }

      const response = await customerService.getTransactions(params);
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pagination.totalPages);
      setTotalCount(response.data.pagination.total);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomerLayout title="Transactions">
        <LoadingSpinner fullScreen />
      </CustomerLayout>
    );
  }

  if (error) {
    return (
      <CustomerLayout title="Transactions">
        <div className="text-center text-red-600">{error}</div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout title="Transactions">
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-card shadow-card p-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-neutral-700">Filter by:</label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-neutral-300 rounded-button focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Transactions</option>
              <option value="debt">Debts Only</option>
              <option value="payment">Payments Only</option>
            </select>
            <span className="text-sm text-neutral-600">
              Total: {totalCount} transactions
            </span>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-card shadow-card">
          {transactions.length > 0 ? (
            <div className="divide-y divide-neutral-200">
              {transactions.map((transaction) => (
                <div key={transaction._id} className="p-6 hover:bg-neutral-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-12 h-12 rounded-button flex items-center justify-center ${
                          transaction.type === 'debt'
                            ? 'bg-red-100'
                            : 'bg-green-100'
                        }`}
                      >
                        <FiDollarSign
                          className={`w-6 h-6 ${
                            transaction.type === 'debt'
                              ? 'text-red-600'
                              : 'text-green-600'
                          }`}
                        />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-neutral-900 capitalize">
                          {transaction.type}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {new Date(transaction.createdAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {transaction.description && (
                          <p className="text-sm text-neutral-500 mt-1">
                            {transaction.description}
                          </p>
                        )}
                        {transaction.paymentMethod && transaction.paymentMethod !== 'n/a' && (
                          <p className="text-xs text-neutral-400 mt-1 capitalize">
                            Method: {transaction.paymentMethod.replace('_', ' ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-2xl font-bold ${
                          transaction.type === 'debt'
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {transaction.type === 'debt' ? '+' : '-'}
                        {transaction.amount.toFixed(3)} BHD
                      </p>
                      <p className="text-sm text-neutral-600 mt-1">
                        Balance: {transaction.balanceAfter.toFixed(3)} BHD
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-neutral-500">
              No transactions found
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-card shadow-card p-4 flex items-center justify-between">
            <div className="text-sm text-neutral-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-neutral-300 rounded-button hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-neutral-300 rounded-button hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default CustomerTransactions;
